import type {BranchAndTagCountData} from '@github-ui/code-view-shared/contexts/BranchAndTagContext'
import type {RecentlyTouchedBranches} from '@github-ui/code-view-shared/contexts/RecentlyTouchedBranchesContext'
import type {DataRouterFileOverviewPayload} from '@github-ui/code-view-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

import {codeViewAppBuilder} from '../config/app-builder'
import {latestCommitQuery} from '../queries/latest-commit-query'
import {isOverviewDataEnabled, overviewDataQuery} from '../queries/overview-data-query'
import {sidebarAboutQuery} from '../queries/sidebar-about-query'
import {treeCommitInfoQuery} from '../queries/tree-commit-info-query'

export function buildCodeViewRepoRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('codeViewRepoRoute', {
    path: '/:owner/:repo',
    index: true,
    queries: [
      mainQuery<DataRouterFileOverviewPayload>({
        // Ask the server to embed sidebar About data in the main payload (overview.sidebarAboutData)
        // instead of making a separate /_sidebar/about request. The sidebarAbout query below is
        // disabled under the same flag, so the decision lives entirely on the client/UI Service.
        queryDeps: ({pathname}) => ({
          pathname,
          searchParams: {
            // eslint-disable-next-line camelcase -- Rails param name
            include_sidebar_about: isFeatureEnabled('code_view_embed_overview_sidebar_about') ? '1' : null,
          },
        }),
      }),
      sidebarAboutQuery,
      latestCommitQuery,
      treeCommitInfoQuery,
      overviewDataQuery,
      {
        queryName: 'recentlyTouchedBranches',
        queryDeps: ({params}) => ({pathname: `/${params.owner}/${params.repo}/recently-touched-branches`}),
        queryFn: async queryKey => queryFnFetch<RecentlyTouchedBranches>(queryKey),
        type: QueryRouteQueryType.Deferred,
        enabled: () => !isOverviewDataEnabled(),
      },
      {
        queryName: 'branchAndTagCount',
        queryDeps: ({params}) => ({pathname: `/${params.owner}/${params.repo}/branch-and-tag-count`}),
        queryFn: async queryKey => queryFnFetch<BranchAndTagCountData>(queryKey),
        type: QueryRouteQueryType.Deferred,
        enabled: () => !isOverviewDataEnabled(),
      },
    ],
  })
}

export const codeViewRepoRoute = buildCodeViewRepoRoute(codeViewAppBuilder)
